import { useScopeStream } from '../hooks/useScopeStream';
import { StepPipeline } from './StepPipeline';
import { ScreenshotStrip } from './ScreenshotStrip';
import { RecipeViewer } from './RecipeViewer';

export interface ScopingPanelProps {
  sessionId: string;
  rescopeReason?: string | null;
  onAccept?: () => void;
  onReject?: () => void;
}

export function ScopingPanel({ sessionId, rescopeReason, onAccept, onReject }: ScopingPanelProps) {
  const stream = useScopeStream(sessionId);

  return (
    <div className="space-y-4">
      {rescopeReason && (
        <div className="rounded-md border border-amber-700 bg-amber-950/40 px-3 py-2 text-sm text-amber-200">
          Rescope triggered: <strong>{rescopeReason}</strong>
        </div>
      )}
      {stream.error && (
        <div className="rounded-md border border-rose-700 bg-rose-950/40 px-3 py-2 text-sm text-rose-200">
          {stream.error}
        </div>
      )}

      <StepPipeline phases={stream.phases} />

      {stream.budget && (
        <div className="grid grid-cols-3 gap-2 text-xs text-slate-300">
          <div className="rounded border border-slate-700 px-2 py-1">
            <div className="text-slate-500">AI calls</div>
            <div>{stream.budget.aiCalls} <span className="text-slate-500">used / {Math.round(stream.budget.aiCalls + stream.budget.remaining.aiCalls)} budget</span></div>
          </div>
          <div className="rounded border border-slate-700 px-2 py-1">
            <div className="text-slate-500">Wall clock</div>
            <div>{Math.round(stream.budget.durationMs / 60000)} min</div>
          </div>
          <div className="rounded border border-slate-700 px-2 py-1">
            <div className="text-slate-500">Approx cost</div>
            <div>${stream.budget.approxCostUsd.toFixed(2)}</div>
          </div>
        </div>
      )}

      {Object.keys(stream.coverage).length > 0 && (
        <div className="rounded border border-slate-700 p-2">
          <div className="text-xs text-slate-500 mb-1">Coverage</div>
          <div className="flex flex-wrap gap-1 text-xs">
            {Object.entries(stream.coverage).map(([type, item]) => {
              const cls = item.status === 'found' ? 'bg-emerald-900 text-emerald-200' :
                          item.status === 'not_exposed' ? 'bg-slate-700 text-slate-400' :
                          'bg-slate-800 text-slate-500';
              return <span key={type} className={`px-2 py-0.5 rounded ${cls}`}>{type}</span>;
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded border border-slate-700 p-2">
          <div className="text-xs text-slate-500 mb-1">Recent actions</div>
          <div className="text-xs space-y-1 max-h-48 overflow-y-auto">
            {stream.actions.slice(-20).map((a) => (
              <div key={a.id} className={`flex gap-2 ${a.decision === 'deny' ? 'text-rose-300' : 'text-slate-300'}`}>
                <span className="text-slate-500">[{a.phase}]</span>
                <span className="font-mono">{a.action}</span>
                <span>— {a.intent}</span>
              </div>
            ))}
          </div>
        </div>
        <ScreenshotStrip screenshots={stream.screenshots} />
      </div>

      {stream.recipeYaml && (
        <RecipeViewer
          yaml={stream.recipeYaml}
          sessionId={sessionId}
          onAccepted={() => onAccept?.()}
          onRejected={() => onReject?.()}
        />
      )}
    </div>
  );
}
